import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {inject, Injectable} from "@angular/core";
import {Store} from "@ngrx/store";
import {take} from "rxjs";
import {ASSET_MANAGEMENT_URL} from "../../../app.config";
import {API_ENDPOINTS, APP_CONSTANTS} from "../../../app.constants";
import {ToastSeverityEnum} from "../../../enums";
import {prepareFiltersForParams} from "../../../helpers";
import {DtoAssetInterface, DtoAssetResultsInterface} from "../../../interfaces";
import {mapAssetFromDto, mapAssetFromDtoArray} from "../../../mapping";
import {setAssets, setAssetsPaginationInfo, setPaginatedAssets} from "../../../store/actions/assets.actions";
import {selectAssetFilters} from "../../../store/reducers/assets.reducer";
import {ToastsService} from "../../app-services";

const pageSize = APP_CONSTANTS.PAGINATION_PAGE_SIZE;

@Injectable({
  providedIn: "root"
})
export class AssetsService {
  private readonly httpClient = inject(HttpClient);
  private readonly toastsService = inject(ToastsService);
  private readonly assetManagementUrl = inject(ASSET_MANAGEMENT_URL);
  private readonly store = inject(Store);

  private _timer?: number;
  private _debounce = true;

  constructor() {
    this.store.select(selectAssetFilters).subscribe(() => {
      if (this._debounce) {
        this._timer = window.setTimeout(() => {
          if (this._timer) {
            clearTimeout(this._timer);
          }
          this.getAssetsPaginated();
        }, APP_CONSTANTS.DEBOUNCE_TIME);
      } else {
        this.getAssetsPaginated();
      }
    });
  }

  public getAsset(id: string): void {
    const idEncoded = encodeURIComponent(id);
    this.httpClient.get<DtoAssetInterface>(this.assetManagementUrl + API_ENDPOINTS.ASSETS + `/${idEncoded}`).subscribe({
      next: data => {
        this.store.dispatch(setAssets({assets: [mapAssetFromDto(data)]}));
      },
      error: (error: HttpErrorResponse) => {
        this.toastsService.addToast({severity: ToastSeverityEnum.Error, message: error.message});
      }
    });
  }

  public getAssetsPaginated(): void {
    this.store
      .select(selectAssetFilters)
      .pipe(take(1))
      .subscribe(filters => {
        const queryParams = prepareFiltersForParams(filters);
        this.httpClient
          .get<DtoAssetResultsInterface>(this.assetManagementUrl + API_ENDPOINTS.ASSETS, {params: queryParams})
          .subscribe({
            next: data => {
              this.store.dispatch(setPaginatedAssets({assets: mapAssetFromDtoArray(data.results ?? [])}));
              this.store.dispatch(
                setAssetsPaginationInfo({
                  paginationInfo: {
                    from: (data.offset ?? 0) + 1,
                    to: (data.offset ?? 0) + (data.count ?? 0),
                    totalResults: data.totalCount ?? 0,
                    currentPage: (data.offset ?? 0) / pageSize + 1,
                    totalPages: Math.ceil((data.totalCount ?? 0) / pageSize)
                  }
                })
              );
            },
            error: (error: HttpErrorResponse) => {
              this.toastsService.addToast({
                severity: ToastSeverityEnum.Error,
                message: "Error fetching Assets Paginated, Status " + error.status
              });
            }
          });
      });
  }
}
